import { appendAppLog } from '../../platform/appLog';
import { autoUpload, meldeWecker, wiederholeBisRuhe } from '../../platform/anstoss';
import { istNetzausfall } from '../verbindung';
import { APP_VERSION, geraeteName } from '../../platform/appVersion';
import { ladestandClassic } from '../../platform/batterie';
import { istGeraet } from '../../platform/gleis';
import { Helligkeit } from '../../platform/helligkeit';
import { ladeGemischt } from '../geraeteEinstellungen';
import { classicConf } from './conf';
import { useLocaleStore } from '../../i18n';
import { Signing } from '../../platform/signing';
import { getStore } from '../../persistence/store';
import { baueClient, istEingerichtet, ladeZugang } from '../zugang';
// ⚠️ Der Server-Teil greift hier ausnahmsweise in die Oberfläche: `useToastStore` ist ein
// gewöhnlicher Zustand-Speicher, kein Bauteil, und die Nachricht des Prüfungsleiters hat kein
// anderes Ziel. Der Weg über den Anwendungsspeicher wäre ein Feld mehr, das niemand sonst liest.
import { useToastStore } from '../../ui/Toast';
import { erfuelleAnforderungen, sendeOffeneDateien } from './anforderungen';
import { wandleFuerClassic } from '../../platform/classicWandel';
import { skizzeAlsPng } from '../../platform/skizzePng';
import { createClassicSender } from './senden';
import type { Taktmelder } from '../takt';

/**
 * Der 10-Sekunden-Takt des Classic-Modus.
 *
 * ⚠️ **Er läuft immer, nicht nur bei offenem Bogen.** `POST /rest/log` ist zugleich der
 * Herzschlag: der Server legt den Eintrag eines Geräts erst an, wenn ein Rumpf eintrifft.
 * Taktete nur der Bogen, verschwände ein iPad aus der Konsole des Prüfungsleiters, sobald der
 * Prüfer zwischen zwei Prüflingen wartet — also genau dann, wenn er nachsieht, ob alle Geräte
 * da sind.
 *
 * Der Zeitgeber steht hier und nicht in `senden.ts`: der Lauf selbst bleibt so ohne Uhr
 * prüfbar (dieselbe Bauart wie `autosave.ts`).
 */

/** Wie am alten Gerät (`load_interval`). */
export const TAKT_MS = 10000;

export interface TaktDeps {
  /**
   * Die Gerätezeile, unter der die Zugangsdaten liegen. Ohne sie liest `ladeZugang` nur die
   * verwalteten Einstellungen des iPads — im Browser also gar nichts.
   */
  clientId: string;
  /** Einschiebbar für Tests; sonst der echte Takt. */
  intervall?: number;
  jetztSenden?: () => Promise<{ nachricht: string | null }>;
  /**
   * Wohin der Takt seinen Stand meldet — die Rückstandspille in der Kopfzeile. Hineingereicht
   * und nicht importiert, aus demselben Grund wie im Relay-Takt (`server/takt.ts`).
   */
  melder?: Taktmelder;
}

/**
 * Startet den Takt und gibt die Funktion zum Beenden zurück.
 *
 * Ein Durchgang, der scheitert, **beendet den Takt nicht**: der nächste versucht es erneut.
 * Ein Prüfungstag ohne Netz darf nicht dazu führen, dass die Übertragung für den Rest des
 * Tages steht, weil einmal etwas schiefging.
 */
export function starteClassicTakt(deps: TaktDeps): () => void {
  let gestoppt = false;
  let laufend: ReturnType<typeof createClassicSender> | null = null;
  /** Das selbsttätige Nachschieben offener Aufnahmen — einmal gebaut, wie der Sender. */
  let nachschieben: ReturnType<typeof sendeOffeneDateien> | null = null;
  /** Gilt für den laufenden Durchgang: von Hand ausgelöst oder `auto_upload` an. */
  let senden = true;

  /**
   * ⚠️ **Ein Durchgang darf nicht in den nächsten hineinlaufen.** Der Takt schlägt alle zehn
   * Sekunden; erfüllt er dabei eine Dateianforderung des Prüfungsleiters, dauert der Schlag in
   * einer belegten WLAN-Zelle länger. Ein zweiter Durchgang sähe dieselben offenen Zeilen —
   * ihr `storeDate` entsteht erst nach der Quittung — und schickte sie ein zweites Mal.
   * `wiederholeBisRuhe` hält sie auseinander und merkt sich einen Anstoss währenddessen.
   *
   * Ruht `auto_upload`, gehen Herzschlag, Bewertungen und Anforderungen des Prüfungsleiters
   * weiter; nur das Nachschieben der Dateien ruht. So hielt es die alte Classic-App, und so
   * muss es sein, damit Stage sie ersetzen kann (Entscheidung des Nutzers, 2026-10-09 — löst
   * die vom 2026-10-02 ab, nach der auch die Bewertungen ruhten; im Relay-Modus gilt sie fort).
   */
  const einmal = wiederholeBisRuhe(async (manuell: boolean) => {
    if (gestoppt) return;
    senden = manuell || autoUpload();
    deps.melder?.laeuft(true);
    try {
      if (deps.jetztSenden) {
        const ergebnis = await deps.jetztSenden();
        // Jede Nachricht zeigen, auch eine gleichlautende: der Server liefert jede genau
        // einmal aus, eine Wiederholung ist also eine neue (2026-10-09).
        if (ergebnis.nachricht) {
          // Schreibt ihre Zeile selbst (s. `ui/Toast.tsx`) — hier stand bis zum 2026-09-25 ein
          // eigener `appendAppLog`-Aufruf daneben, der dieselbe Nachricht ein zweites Mal ins
          // Protokoll legte.
          useToastStore.getState().zeigeText(ergebnis.nachricht);
        }
        return;
      }

      const store = getStore();
      const zugang = await ladeZugang(store, deps.clientId);
      if (zugang.modus !== 'classic' || !istEingerichtet(zugang)) return;

      if (!laufend) {
        const identity = await Signing.getIdentity().catch(() => null);
        laufend = baueClassicSender(store, zugang, identity, deps.clientId);
        nachschieben = sendeOffeneDateien({
          zugang: { baseUrl: zugang.url, login: zugang.login, password: zugang.password },
          deviceID: identity?.deviceId ?? '',
          deviceName: geraeteName() || (identity?.deviceName ?? ''),
          store,
          wandle: wandleFuerClassic,
        });
      }

      const ergebnis = await laufend.jetztSenden();

      /*
       * ⚠️ **Nach den Bewertungen, nicht davor** (Vorgabe des Nutzers, 2026-09-22: Aufnahmen
       * gehen hinaus, sobald sie anfallen). Dieselbe Reihenfolge wie im Relay-Takt: erst das
       * Protokoll, dann die Dateien — sonst sähe der Server eine Weile mehr Dateien, als das
       * Protokoll nennt.
       *
       * ⚠️ **Fehlschläge halten den Takt nicht auf.** Sie stehen in der Buchführung und im
       * Protokoll; der nächste Durchgang nimmt sich das Übrige erneut vor.
       */
      if (nachschieben && senden) {
        try {
          await nachschieben();
        } catch (fehler) {
          // Kein Netz steht einmal je Offline-Phase im Log (`../verbindung.ts`).
          if (!istNetzausfall(fehler)) {
            await appendAppLog('Dateien nachschieben (Classic): ' + String(fehler), 'warn');
          }
        }
      }
      if (ergebnis.nachricht) {
        // ⚠️ Der Server liefert eine Nachricht **genau einmal** aus und löscht sie dann. Sie
        // wird deshalb im selben Zug beides: eingeblendet und aufgeschrieben.
        //
        // Beides, nicht nur eines: die Einblendung verschwindet nach zwölf Sekunden, und wer
        // in dem Moment auf den Prüfling sieht statt auf das iPad, findet sie nachher im
        // Protokoll wieder.
        //
        // ⭐ **Das Aufschreiben steht seit dem 2026-09-25 im Speicher selbst** (`ui/Toast.tsx`):
        // jede Einblendung schreibt ihre Zeile, nicht jede Aufrufstelle einzeln. Ein eigener
        // `appendAppLog`-Aufruf hier legte dieselbe Nachricht ein zweites Mal ab.
        useToastStore.getState().zeigeText(ergebnis.nachricht);
      }
    } catch (fehler) {
      // Geschluckt und protokolliert: ein gescheiterter Schlag darf den Takt nicht beenden.
      // Kein Netz steht einmal je Offline-Phase im Log (`../verbindung.ts`).
      if (!istNetzausfall(fehler)) {
        await appendAppLog('Classic-Takt: ' + String(fehler), 'error').catch(() => undefined);
      }
    } finally {
      deps.melder?.laeuft(false);
      // Erst jetzt zählen: der Stand entsteht im Durchgang, nicht davor.
      void deps.melder?.zaehle();
    }
  });
  const schlag = (manuell = false) => einmal(manuell);

  // Ohne `window.`: dieses Modul ist kein Bestandteil der Oberfläche und soll auch in einer
  // Umgebung ohne DOM laufen — sonst liesse es sich nur mit einem nachgebauten Fenster prüfen.
  const id = setInterval(() => void schlag(), deps.intervall ?? TAKT_MS);
  // Sofort senden, sobald etwas abgelegt ist (`platform/anstoss.ts`).
  const abmeldenWecker = meldeWecker(schlag);
  // Nicht erst nach zehn Sekunden: beim Start soll das Gerät sofort in der Konsole erscheinen.
  void schlag();

  return () => {
    gestoppt = true;
    clearInterval(id);
    abmeldenWecker();
    laufend = null;
  };
}

/**
 * Der Classic-Sender, wie ihn Takt **und** das manuelle „Jetzt übertragen“ bauen.
 *
 * ⚠️ **Eine Fabrik für beide Wege** (2026-10-09). Vorher baute die Einstellungsseite ihren
 * eigenen Sender — ohne `erfuelleAnforderungen`: kam eine Anforderung des Prüfungsleiters auf
 * dieser Antwort, wurde sie nur protokolliert und war verloren (der Server liefert sie genau
 * einmal). Dazu fehlten Skizze und Gerätename wich ab.
 */
export function baueClassicSender(
  store: ReturnType<typeof getStore>,
  zugang: Awaited<ReturnType<typeof ladeZugang>>,
  identity: { deviceId?: string; deviceName?: string; osVersion?: string; deviceModel?: string } | null,
  clientId?: string | null,
) {
  const deviceID = identity?.deviceId ?? '';
  const deviceName = geraeteName() || (identity?.deviceName ?? '');
  const anmeldung = { baseUrl: zugang.url, login: zugang.login, password: zugang.password };
  return createClassicSender({
    store,
    client: baueClient(zugang, {
      deviceIdentifier: deviceID,
      deviceName,
    }),
    log: appendAppLog,
    geraet: {
      deviceID,
      deviceName,
      // `{Fassung}@{Plattform}v{OS}` wie die alte App (`2.1.15@iOSv18.6`).
      appVersion: APP_VERSION + '@' + (istGeraet() ? 'iOS' : 'Web') + 'v' + (identity?.osVersion ?? ''),
      model: identity?.deviceModel ?? '',
    },
    batterie: ladestandClassic,
    helligkeit: async () => (istGeraet() ? (await Helligkeit.lies()).wert / 100 : -1),
    conf: async () =>
      clientId ? classicConf(await ladeGemischt(store, clientId), useLocaleStore.getState().locale) : {},
    skizze: skizzeAlsPng,
    // ⚠️ **Nur hier verdrahtet, nicht in `senden.ts`.** Das Erfüllen kennt die Ablage des
    // Geräts; der Übertragungslauf soll sie nicht kennen müssen. Der Zugang wandert mit, weil
    // der Upload seine eigene Verbindung aufmacht (s. `hochladen.ts`).
    erfuelleAnforderungen: erfuelleAnforderungen({
      zugang: anmeldung,
      deviceID,
      deviceName,
      store,
      wandle: wandleFuerClassic,
    }),
  });
}
